import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatDateToKoreanWithWeekday } from 'shared/lib';
import { useGetJsonParams } from 'shared/lib/useGetJsonParams';
import { Tabs } from 'shared/ui';
import { ArrowIcon02, CareDownIcon, CareUpIcon } from 'shared/ui/icon/ArrowIcon';
import emptyIcon from 'styles/images/lab-list__empty-icon.svg';

import { useGetSpecimens } from '../api';
import { getLowHigh } from '../lib/getLowHigh';

import './kareNote.css';

const LAB_TABS = [
  { key: 'all', label: '전체' },
  { key: 'abnormal', label: '이상 수치' },
];

export const Specimens = () => {
  const navigate = useNavigate();

  const { testId, hspId, testDate, resultDate, testName, specimensTab } = useGetJsonParams();

  const { data } = useGetSpecimens({ testId, hspId });

  const [activeTab, setActiveTab] = useState(specimensTab || 'all');

  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current.scrollTo(0, 0);
    const params = Object.fromEntries(new URLSearchParams(window.location.search));
    params.specimensTab = activeTab;
    navigate(`?${new URLSearchParams(params).toString()}`, {
      replace: true,
    });
  }, [activeTab]);

  return (
    <div className='lab-list' ref={scrollRef}>
      {/* 헤더 */}
      <header className='lab-list__header'>
        <h1 className='lab-list__title'>{testName}</h1>

        <div className='lab-list__meta'>
          <span className='lab-list__date'>
            <span className='title'>검사일</span>
            <span className='content'>{formatDateToKoreanWithWeekday(testDate.replace(/-/g, ''))}</span>
          </span>
          {resultDate && (
            <span className='lab-list__date'>
              <span className='title'>결과일</span>
              <span className='content'>{formatDateToKoreanWithWeekday(resultDate.replace(/-/g, ''))}</span>
            </span>
          )}
        </div>
      </header>

      {/* 필터 탭 */}
      <div className='lab-list__tabs tabs-wrap'>
        <Tabs tabs={LAB_TABS} activeTab={activeTab} onChange={setActiveTab} variant='filter' />
      </div>

      {/* 리스트 영역 */}
      {activeTab === 'all' ? (
        <ul className='lab-list__items'>
          {data.specimenResults.map((specimen, idx) => {
            return <TestCard specimen={specimen} key={`${specimen.specimenCode}-${specimen.specimenEnName}-${idx}`} />;
          })}
        </ul>
      ) : (
        (() => {
          const abnormalList = data.specimenResults.filter((specimen) => {
            const { isHigh, isLow } = getLowHigh(
              specimen.referenceMinValue,
              specimen.referenceMaxValue,
              specimen.testResult,
            );
            return isHigh || isLow;
          });
          return abnormalList.length ? (
            <ul className='lab-list__items'>
              {abnormalList.map((specimen, idx) => {
                return (
                  <TestCard specimen={specimen} key={`${specimen.specimenCode}-${specimen.specimenEnName}-${idx}`} />
                );
              })}
            </ul>
          ) : (
            <div className='lab-list__empty'>
              <img src={emptyIcon} alt='이상 수치 없음' className='lab-list__empty-icon' />
              <p className='lab-list__empty-text'>이상 수치인 항목이 없어요!</p>
            </div>
          );
        })()
      )}
    </div>
  );
};

const TestCard = ({ specimen }) => {
  const navigate = useNavigate();

  const { isHigh, isLow } = getLowHigh(specimen.referenceMinValue, specimen.referenceMaxValue, specimen.testResult);
  return (
    <li
      className='lab-list__item'
      key={specimen.specimenCode}
      onClick={() => {
        navigate(
          `/karenote/specimens/${specimen.specimenCode}` +
            window.location.search +
            `&specimenEnName=${specimen.specimenEnName}&specimenKrName=${specimen.specimenKrName}`,
        );
      }}
    >
      <div className='lab-list__item-main'>
        <div className='lab-list__item-title'>
          <strong className='lab-list__name'>{specimen.specimenKrName}</strong>
          <span className='lab-list__en'>{specimen.specimenEnName}</span>
        </div>

        <div className='lab-list__item-value'>
          <span className='lab-list__number'>{specimen.testResult}</span>
          <span className='lab-list__unit'>{specimen.testResultUnit}</span>

          {isLow && (
            <span className='lab-list__status lab-list__status--low'>
              <CareDownIcon />
              <span>낮아요</span>
            </span>
          )}
          {isHigh && (
            <span className='lab-list__status lab-list__status--high'>
              <CareUpIcon />
              <span>높아요</span>
            </span>
          )}
        </div>

        {(specimen.referenceMinValue || specimen.referenceMaxValue) && (
          <div className='lab-list__item-meta'>
            <span className='lab-list__range'>참고치</span>
            <span className='lab-list__range-value'>
              {specimen.referenceMinValue && specimen.referenceMaxValue
                ? `${specimen.referenceMinValue} ~ ${specimen.referenceMaxValue}`
                : (() => {
                    const value = specimen.referenceMinValue || specimen.referenceMaxValue;
                    return value;
                  })()}
            </span>
          </div>
        )}
      </div>

      <span className='lab-list__arrow'>
        <ArrowIcon02 />
      </span>
    </li>
  );
};

